import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, CheckCircle2, Award, Shield, DollarSign } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { stats, whyChooseUs, testimonials, services } from '../data/mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const IconComponent = ({ name, ...props }) => {
    const Icon = LucideIcons[name.split('-').map((word, i) => 
      i === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
    ).join('')] || LucideIcons.Home;
    return <Icon {...props} />;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=80"
            alt="Beautiful home windows"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-dark/95 via-navy-dark/85 to-navy-dark/70"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 pt-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                Transform Your Home with
                <span className="text-gold block mt-2">Premium Windows & Doors</span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Colorado's trusted experts in residential window and door replacement. 
                Combining quality craftsmanship with affordable pricing for over a decade.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="bg-gold text-navy-dark hover:bg-gold-light text-lg px-8 py-6 rounded-lg font-bold shadow-xl hover:shadow-gold/50 transform hover:scale-105 transition-all duration-200 group">
                    Get Free Estimate
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-navy-dark text-lg px-8 py-6 rounded-lg font-bold transition-all duration-200">
                    View Services
                  </Button>
                </Link>
                <Link to="/financing">
                  <Button className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-navy-dark text-lg px-8 py-6 rounded-lg font-bold transition-all duration-200">
                    Financing
                  </Button>
                </Link>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-12">
                {stats.map((stat) => (
                  <div key={stat.id} className="text-center p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-gold/30">
                    <div 
                      className={`text-3xl font-bold mb-1`}
                      style={stat.value === 'HEPA' ? { color: '#ffed4e' } : {}}
                    >
                      <span className={stat.value !== 'HEPA' ? 'text-gold' : ''}>
                        {stat.value}
                      </span>
                    </div>
                    <div className="text-sm text-gray-300">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gold/50 rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-gold rounded-full mt-2 animate-scroll"></div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-navy-dark mb-4">
              Why Choose <span className="text-gold">Savior Windows?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Excellence meets affordability with our commitment to quality, service, and customer satisfaction
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item) => (
              <Card 
                key={item.id} 
                className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-gold/50 transform hover:-translate-y-2"
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-gold/20 to-gold/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent name={item.icon} className="text-gold" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-navy-dark mb-3 group-hover:text-gold transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="py-20 bg-navy-dark text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our <span className="text-gold">Services</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Comprehensive window and door solutions tailored to your home's needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {services.slice(0, 6).map((service) => (
              <Card 
                key={service.id} 
                className="bg-white/5 backdrop-blur-sm border-gold/30 hover:border-gold transition-all duration-300 group hover:bg-white/10"
              >
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-gold/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-gold/30 transition-colors">
                    <IconComponent name={service.icon} className="text-gold" size={28} />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gold group-hover:text-white transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.slice(0, 3).map((feature) => (
                      <li key={`${service.id}-${feature}`} className="flex items-center gap-2 text-sm text-gray-400">
                        <CheckCircle2 size={16} className="text-gold flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Link to="/services">
              <Button className="bg-gold text-navy-dark hover:bg-gold-light text-lg px-10 py-6 rounded-lg font-bold shadow-xl hover:shadow-gold/50 transform hover:scale-105 transition-all duration-200">
                View All Services
                <ArrowRight className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-navy-dark mb-4">
              What Our <span className="text-gold">Customers Say</span>
            </h2>
            <p className="text-xl text-gray-600">
              Real experiences from Colorado homeowners
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial) => (
              <Card 
                key={testimonial.id} 
                className="hover:shadow-2xl transition-all duration-300 border-2 hover:border-gold/50"
              >
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`${testimonial.id}-star-${i}`} className="text-gold fill-gold" size={20} />
                    ))}
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-6 italic">
                    "{testimonial.text}"
                  </p>
                  <div className="border-t border-gray-200 pt-4">
                    <p className="font-bold text-navy-dark">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.location}</p>
                    <p className="text-xs text-gray-400 mt-1">{testimonial.date}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-navy-dark to-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_e4501b70-3916-4f5c-9cb2-b91d80cef241/artifacts/ywaw69v6_Main%20Circle%20Cut.png"
            alt="Background"
            className="w-full h-full object-cover brightness-110"
            style={{ mixBlendMode: 'soft-light' }}
          />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Home?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto">
            Get your free, no-obligation estimate today. Our experts will assess your needs 
            and provide transparent pricing with no hidden fees.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-gold text-navy-dark hover:bg-gold-light text-lg px-10 py-6 rounded-lg font-bold shadow-xl hover:shadow-gold/50 transform hover:scale-105 transition-all duration-200 group">
                Schedule Free Consultation
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <a href="tel:7202989993">
              <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-navy-dark text-lg px-10 py-6 rounded-lg font-bold transition-all duration-200">
                Call (720) 298-9993
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
